import { useEffect, useRef, useState } from 'react';
import { PlusIcon, XIcon } from './icons.js';
import {
  ALL_SPECIALISATIONS,
  CANONICAL_PRODUCT_SPECIALISATIONS,
  CANONICAL_SPECIALISATIONS,
} from '@kennisbank/shared';

/**
 * Inline-edit specialisations chip cell. Used by the /documenten admin
 * row (cap = 10 per the curation rules).
 *
 * Behaviour:
 *   - Each existing tag renders as a removable mosterdgeel chip
 *   - Below the cap (or always, when no cap), a dashed "+ tag" button
 *     opens a popover listing not-yet-selected tags. When `ring` is set
 *     the popover is scoped to that ring's vocabulary (ring0 = HR,
 *     ring1 = product); without `ring` it falls back to the full union.
 *   - Outside-click on the popover closes it
 *   - `saving` disables interaction during in-flight PATCH so the user
 *     can't double-submit on a slow network
 *   - When `value` is empty AND the picker is closed, render an em-dash
 *     so empty rows visually flag "nothing set" instead of looking
 *     like a silent gap
 */
export function InlineSpecialisationsCell({
  value,
  saving,
  onSave,
  maxItems,
  ring,
  availableNames,
}: {
  value: string[];
  saving: boolean;
  onSave: (next: string[]) => void | Promise<void>;
  /** Cap on number of tags. Omit for unlimited. */
  maxItems?: number;
  /** Doc's ring — scopes the picker to ring0 HR or ring1 product tags. */
  ring?: 'ring0' | 'ring1';
  /** Optional override vocabulary, e.g. the live list fetched from
   *  /api/v1/admin/specialisations. When supplied, the picker shows
   *  these names instead of the hardcoded CANONICAL_* arrays so
   *  admin-defined specs appear immediately. The `ring` prop is
   *  ignored when this is set — the caller is expected to pre-filter. */
  availableNames?: readonly string[];
}): JSX.Element {
  const [pickerOpen, setPickerOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const onClick = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [pickerOpen]);

  const atCap = maxItems !== undefined && value.length >= maxItems;

  const remove = async (tag: string): Promise<void> => {
    await onSave(value.filter((s) => s !== tag));
  };
  const add = async (tag: string): Promise<void> => {
    if (value.includes(tag) || atCap) return;
    await onSave([...value, tag]);
    setPickerOpen(false);
  };

  const vocabulary = availableNames
    ? availableNames
    : ring === 'ring0'
      ? CANONICAL_SPECIALISATIONS
      : ring === 'ring1'
        ? CANONICAL_PRODUCT_SPECIALISATIONS
        : ALL_SPECIALISATIONS;
  const remaining = vocabulary.filter((s) => !value.includes(s));

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex flex-wrap items-center gap-1">
        {value.map((s) => (
          <span
            key={s}
            className="inline-flex items-center gap-1 rounded-full bg-mosterdgeel-50 px-2 py-0.5 text-[11px] font-medium text-mosterdgeel-600"
          >
            {s}
            <button
              type="button"
              onClick={() => void remove(s)}
              disabled={saving}
              aria-label={`Remove ${s}`}
              title={`Remove ${s}`}
              className="rounded-full text-mosterdgeel-600/70 hover:bg-mosterdgeel-100 hover:text-mosterdgeel-700 disabled:opacity-40"
            >
              <XIcon size={11} />
            </button>
          </span>
        ))}
        {!atCap && (
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            disabled={saving}
            title="Add specialisation"
            className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-brand-300 bg-white px-2 py-0.5 text-[11px] font-medium text-brand-700 hover:border-brand-500 hover:bg-brand-50 disabled:opacity-50"
          >
            <PlusIcon size={11} /> tag
          </button>
        )}
        {value.length === 0 && !pickerOpen && <span className="text-ink-900/30">—</span>}
      </div>
      {pickerOpen && remaining.length > 0 && (
        <div className="absolute left-0 top-full z-20 mt-1 max-h-48 w-56 overflow-y-auto rounded-md border border-brand-200 bg-white p-1 shadow-lg">
          {remaining.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => void add(s)}
              className="block w-full rounded px-2 py-1 text-left text-xs text-ink-900 hover:bg-brand-50"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
